import { useEffect, useMemo, useState } from 'react';
import {
	Alert, Box, Button, Card, CardContent, CardHeader, CircularProgress, FormControl, FormControlLabel,
	FormLabel, Grid, Radio, RadioGroup, Stack, TextField, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { smtpApi } from '@/services/smtpApi';
import {
	SMTP_PORT_STARTTLS, SMTP_PORT_TLS, SmtpTestSchema, SmtpUpdateSchema, type SmtpPublic,
} from '@/schemas';
import { getFieldError, validateForm } from '@/schemas/forms';
import { describeMailError } from '@/lib/errorCodes';
import { useAppSelector } from '@/store/hooks';

const QK = ['admin', 'smtp'] as const;

interface SmtpFormState {
	host: string;
	port: string;
	secure: boolean;
	user: string;
	pass: string;
	fromName: string;
	fromAddress: string;
}

type SavedFields = Omit<SmtpFormState, 'pass'>;

const EMPTY_FORM: SmtpFormState = {
	host: '', port: String(SMTP_PORT_STARTTLS), secure: false, user: '', pass: '', fromName: '', fromAddress: '',
};

const toSaved = (s: SmtpPublic): SavedFields => ({
	host: s.host, port: String(s.port), secure: s.secure, user: s.user, fromName: s.fromName, fromAddress: s.fromAddress,
});

const SAVED_KEYS = ['host', 'port', 'secure', 'user', 'fromName', 'fromAddress'] as const;

const norm = (v: string | boolean) => (typeof v === 'string' ? v.trim() : v);

export const SmtpSettingsCard = () => {
	const qc = useQueryClient();
	const adminEmail = useAppSelector((state) => state.user.user?.email ?? '');
	const { data, isLoading, error: loadError } = useQuery({ queryKey: QK, queryFn: smtpApi.get });

	const [form, setForm] = useState<SmtpFormState>(EMPTY_FORM);
	const [saved, setSaved] = useState<SavedFields | null>(null);
	const [hasPassword, setHasPassword] = useState(false);
	const [formErrors, setFormErrors] = useState<z.ZodError | null>(null);
	const [testTo, setTestTo] = useState(adminEmail);
	const [testError, setTestError] = useState<z.ZodError | null>(null);

	useEffect(() => {
		if (data === undefined) return;
		if (data) {
			setForm({ ...toSaved(data), pass: '' });
			setSaved(toSaved(data));
			setHasPassword(data.hasPassword);
		} else {
			setSaved(null);
		}
	}, [data]);

	useEffect(() => {
		setTestTo((prev) => prev || adminEmail);
	}, [adminEmail]);

	// The test mail is always sent with the STORED config, so testing unsaved edits would be misleading.
	const isDirty = useMemo(() => {
		if (!saved) return true;
		if (form.pass) return true;
		return SAVED_KEYS.some((key) => norm(form[key]) !== norm(saved[key]));
	}, [form, saved]);

	const testMut = useMutation({ mutationFn: smtpApi.test });

	const saveMut = useMutation({
		mutationFn: smtpApi.update,
		onSuccess: (next) => {
			// Set form state here: when the saved values equal the cached ones, React Query keeps the old
			// `data` reference (structural sharing), so the effect above would not clear the typed password.
			setForm((prev) => ({ ...prev, pass: '' }));
			setSaved(toSaved(next));
			setHasPassword(next.hasPassword);
			qc.setQueryData(QK, next);
			testMut.reset();
		},
	});

	const update = <K extends keyof SmtpFormState>(key: K, value: SmtpFormState[K]) => {
		setForm((prev) => ({ ...prev, [key]: value }));
		if (formErrors) setFormErrors(null);
		saveMut.reset();
	};

	const handleSecureChange = (secure: boolean) => {
		setForm((prev) => {
			const port = Number(prev.port);
			// Only follow the standard ports; a relay on a custom port keeps what the admin typed.
			const isStandard = !prev.port || port === SMTP_PORT_TLS || port === SMTP_PORT_STARTTLS;
			const nextPort = isStandard ? String(secure ? SMTP_PORT_TLS : SMTP_PORT_STARTTLS) : prev.port;
			return { ...prev, secure, port: nextPort };
		});
		saveMut.reset();
	};

	const handleSave = () => {
		const validation = validateForm(SmtpUpdateSchema, {
			host: form.host,
			port: form.port.trim() === '' ? Number.NaN : Number(form.port),
			secure: form.secure,
			user: form.user,
			pass: form.pass ? form.pass : undefined,
			fromName: form.fromName,
			fromAddress: form.fromAddress,
		});
		if (!validation.success || !validation.data) {
			setFormErrors(validation.errors ?? null);
			return;
		}
		setFormErrors(null);
		saveMut.mutate(validation.data);
	};

	const handleTest = () => {
		const to = testTo.trim();
		const validation = validateForm(SmtpTestSchema, to ? { to } : {});
		if (!validation.success || !validation.data) {
			setTestError(validation.errors ?? null);
			return;
		}
		setTestError(null);
		testMut.mutate(validation.data);
	};

	if (isLoading) {
		return <Card variant="outlined"><CardContent><CircularProgress size={20} /></CardContent></Card>;
	}
	if (loadError) {
		return (
			<Card variant="outlined">
				<CardContent>
					<Alert severity="error">{describeMailError(loadError, 'Không tải được cấu hình SMTP.')}</Alert>
				</CardContent>
			</Card>
		);
	}

	const fieldError = (field: string) => getFieldError(formErrors, field);
	const testDisabled = isDirty || testMut.isPending;

	return (
		<Card variant="outlined">
			<CardHeader
				title="Cấu hình gửi email (SMTP)"
				subheader="Dùng để gửi mã đặt lại mật khẩu cho người dùng."
			/>
			<CardContent>
				<Stack spacing={2}>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={8}>
							<TextField label="Máy chủ SMTP" size="small" fullWidth placeholder="smtp.gmail.com"
								autoComplete="off" value={form.host} onChange={(e) => update('host', e.target.value)}
								error={!!fieldError('host')} helperText={fieldError('host')} />
						</Grid>
						<Grid item xs={12} sm={4}>
							<TextField label="Cổng" size="small" fullWidth type="number"
								inputProps={{ min: 1, max: 65535 }}
								value={form.port} onChange={(e) => update('port', e.target.value)}
								error={!!fieldError('port')} helperText={fieldError('port')} />
						</Grid>
						<Grid item xs={12}>
							<FormControl>
								<FormLabel id="smtp-secure-label">Bảo mật kết nối</FormLabel>
								<RadioGroup row aria-labelledby="smtp-secure-label"
									value={form.secure ? 'tls' : 'starttls'}
									onChange={(e) => handleSecureChange(e.target.value === 'tls')}>
									<FormControlLabel value="tls" control={<Radio size="small" />}
										label={`TLS (cổng ${SMTP_PORT_TLS})`} />
									<FormControlLabel value="starttls" control={<Radio size="small" />}
										label={`STARTTLS (cổng ${SMTP_PORT_STARTTLS})`} />
								</RadioGroup>
							</FormControl>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField label="Tài khoản" size="small" fullWidth autoComplete="off"
								value={form.user} onChange={(e) => update('user', e.target.value)}
								error={!!fieldError('user')} helperText={fieldError('user')} />
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField label="Mật khẩu" size="small" fullWidth type="password" autoComplete="new-password"
								placeholder={hasPassword ? '••••••••' : ''}
								value={form.pass} onChange={(e) => update('pass', e.target.value)}
								error={!!fieldError('pass')}
								helperText={fieldError('pass')
									?? (hasPassword ? 'Đã lưu mật khẩu. Để trống để giữ mật khẩu cũ.' : 'Chưa lưu mật khẩu.')} />
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField label="Tên người gửi" size="small" fullWidth autoComplete="off"
								value={form.fromName} onChange={(e) => update('fromName', e.target.value)}
								error={!!fieldError('fromName')} helperText={fieldError('fromName')} />
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField label="Địa chỉ gửi" size="small" fullWidth type="email" autoComplete="off"
								placeholder="noreply@example.com"
								value={form.fromAddress} onChange={(e) => update('fromAddress', e.target.value)}
								error={!!fieldError('fromAddress')} helperText={fieldError('fromAddress')} />
						</Grid>
					</Grid>

					{saveMut.error && <Alert severity="error">{describeMailError(saveMut.error, 'Lưu cấu hình thất bại.')}</Alert>}
					{saveMut.isSuccess && <Alert severity="success">Đã lưu cấu hình SMTP.</Alert>}
					<Box>
						<Button variant="contained" startIcon={<SaveIcon />} disabled={saveMut.isPending} onClick={handleSave}>
							{saveMut.isPending ? 'Đang lưu…' : 'Lưu'}
						</Button>
					</Box>

					<Typography variant="subtitle2" sx={{ pt: 1 }}>Gửi email thử</Typography>
					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
						<TextField label="Gửi thử tới" size="small" type="email" autoComplete="off"
							sx={{ flex: 1 }}
							value={testTo}
							onChange={(e) => { setTestTo(e.target.value); if (testError) setTestError(null); testMut.reset(); }}
							error={!!getFieldError(testError, 'to')}
							helperText={getFieldError(testError, 'to')
								?? (isDirty ? 'Lưu cấu hình trước khi gửi thử: email thử dùng cấu hình đã lưu.' : undefined)} />
						<Button variant="outlined" startIcon={<SendIcon />} disabled={testDisabled} onClick={handleTest}>
							{testMut.isPending ? 'Đang gửi…' : 'Gửi thử'}
						</Button>
					</Stack>
					{testMut.error && <Alert severity="error">{describeMailError(testMut.error, 'Gửi email thử thất bại.')}</Alert>}
					{testMut.isSuccess && <Alert severity="success">Đã gửi email thử. Kiểm tra hộp thư nhận.</Alert>}
				</Stack>
			</CardContent>
		</Card>
	);
};
